import MomentUtils from '@date-io/moment';
import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Step,
	StepLabel,
	Stepper,
	TextField,
	Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert, AlertTitle } from '@material-ui/lab';
import { KeyboardDatePicker, KeyboardDateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { logEvent } from 'components/telemetry';
import { DATE_TIME_FIELD_VALUE_FORMAT } from 'components/DateTimeField';
import {
	buildChangePlan,
	BulkChangePlan,
	BulkFieldDefinition,
	BulkOperationId,
	describeChange,
	describeDuration,
	estimateDurationMs,
	findOperation,
	invertPlan,
	isUndoable,
	planToUndoWrites,
	planToWrites,
	selectableRows,
} from 'helpers/bulkEdit/changePlan';
import { BulkRunProgress, BulkRunResult, runBulkWrites } from 'helpers/bulkEdit/runEngine';
import { Moment } from 'moment';
import React from 'react';
import { anumatiBulkFields, findBulkField } from './fields';
import { RunProgressView, RunResultAlert } from './BulkRunView';
import PreviewTable, { RowRunStatus } from './PreviewTable';

/** Above this many records the dialog asks the user to look twice before continuing. */
const LARGE_SET_THRESHOLD = 200;

// The app lifts Bootstrap's .modal to 10040 and Material pins its own modals at 1300, so
// the dialog is raised above both. Menus and pickers are separate Material modals, so they
// need their own level above the dialog or they open behind it.
const DIALOG_Z_INDEX = 10050;
const OVERLAY_Z_INDEX = DIALOG_Z_INDEX + 10;

/** Menus and pickers opened from inside the dialog share this placement. */
const overlayProps = { style: { zIndex: OVERLAY_Z_INDEX } };

const STEPS = ['Choose change', 'Review', 'Apply'];

export interface BulkEditContext {
	records: any[];
	filters: string[];
	model: any;
}

interface Props {
	open: boolean;
	onClose: () => void;
	getContext: () => BulkEditContext;
	onApplied: () => void;
}

const useStyles = makeStyles((theme) => ({
	section: {
		marginBottom: theme.spacing(2),
	},
	chips: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: 6,
	},
	counts: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: 8,
		marginBottom: theme.spacing(1),
	},
	table: {
		border: '1px solid rgba(0, 0, 0, 0.12)',
		borderRadius: 4,
	},
}));

type WizardStep = 'build' | 'preview' | 'run' | 'result';

function useBulkEditState(open: boolean, getContext: () => BulkEditContext) {
	let [context, setContext] = React.useState<BulkEditContext | null>(null);

	React.useEffect(() => {
		if (open) {
			setContext(getContext());
		} else {
			setContext(null);
		}
		// getContext is stable for the lifetime of the dialog's owner.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	return context;
}

export function BulkEditDialog({ open, onClose, getContext, onApplied }: Props) {
	let classes = useStyles();
	let context = useBulkEditState(open, getContext);

	let [step, setStep] = React.useState<WizardStep>('build');
	let [fieldKey, setFieldKey] = React.useState<string>(anumatiBulkFields[0].key!);
	let [operation, setOperation] = React.useState<BulkOperationId>(anumatiBulkFields[0].operations[0].id);
	let [input, setInput] = React.useState('');
	let [pickerValue, setPickerValue] = React.useState<Moment | null>(null);
	let [plan, setPlan] = React.useState<BulkChangePlan | null>(null);
	/** Set while an undo runs, so the table shows the reversal and not the original edit. */
	let [undoPlan, setUndoPlan] = React.useState<BulkChangePlan | null>(null);
	let [excluded, setExcluded] = React.useState<{ [id: string]: boolean }>({});
	let [typedConfirm, setTypedConfirm] = React.useState('');
	let [progress, setProgress] = React.useState<BulkRunProgress | null>(null);
	let [result, setResult] = React.useState<BulkRunResult | null>(null);
	let [undoResult, setUndoResult] = React.useState<BulkRunResult | null>(null);
	let [running, setRunning] = React.useState(false);
	let [rowStatus, setRowStatus] = React.useState<{ [id: string]: RowRunStatus }>({});
	let [rowErrors, setRowErrors] = React.useState<{ [id: string]: string }>({});

	let cancelRef = React.useRef(false);

	let definition: BulkFieldDefinition = findBulkField(fieldKey) ?? anumatiBulkFields[0];
	let operationDef = findOperation(definition, operation) ?? definition.operations[0];
	let records = context?.records ?? [];

	React.useEffect(() => {
		if (open) {
			setStep('build');
			setPlan(null);
			setUndoPlan(null);
			setExcluded({});
			setTypedConfirm('');
			setProgress(null);
			setResult(null);
			setUndoResult(null);
			setRunning(false);
			setRowStatus({});
			setRowErrors({});
			cancelRef.current = false;
		}
	}, [open]);

	function onFieldChange(key: string) {
		let next = findBulkField(key) ?? anumatiBulkFields[0];
		setFieldKey(key);
		setOperation(next.operations[0].id);
		setInput('');
		setPickerValue(null);
	}

	function onOperationChange(next: BulkOperationId) {
		setOperation(next);
		setInput('');
		setPickerValue(null);
	}

	let validationError = React.useMemo(() => {
		if (!operationDef.inputType) {
			return null;
		}

		if (!input?.trim()) {
			return 'Enter a value';
		}

		return definition.validateInput?.(operation, input) ?? null;
	}, [definition, operation, operationDef, input]);

	let includedCount = plan ? selectableRows(plan).filter((row) => !excluded[row.id]).length : 0;

	let destructiveReady = !plan?.destructive || typedConfirm.trim() === String(includedCount);

	function buildPreview() {
		if (!records.length) {
			return;
		}

		let nextPlan = buildChangePlan(records, definition, operation, input);
		setPlan(nextPlan);
		setUndoPlan(null);
		setExcluded({});
		setTypedConfirm('');
		setStep('preview');
	}

	function toggleRow(id: string) {
		setExcluded((current) => ({ ...current, [id]: !current[id] }));
	}

	function setAllRows(include: boolean) {
		if (!plan) {
			return;
		}

		let next: { [id: string]: boolean } = {};

		if (!include) {
			selectableRows(plan).forEach((row) => {
				next[row.id] = true;
			});
		}

		setExcluded(next);
	}

	/** Tracking callbacks shared by the apply and undo runs. */
	function trackingOptions(writes: { id: string }[]) {
		let initial: { [id: string]: RowRunStatus } = {};
		writes.forEach((write) => {
			initial[write.id] = 'pending';
		});
		setRowStatus(initial);
		setRowErrors({});

		function mark(ids: string[], status: RowRunStatus) {
			setRowStatus((current) => {
				let next = { ...current };
				ids.forEach((id) => {
					next[id] = status;
				});
				return next;
			});
		}

		return {
			onChunkStart: (ids: string[]) => mark(ids, 'writing'),
			onChunkRetry: (ids: string[]) => mark(ids, 'retrying'),
			onChunkSettled: (succeeded: string[], failed: { id: string; error: string }[]) => {
				mark(succeeded, 'done');
				mark(
					failed.map((failure) => failure.id),
					'failed'
				);
				setRowErrors((current) => {
					let next = { ...current };
					failed.forEach((failure) => {
						next[failure.id] = failure.error;
					});
					return next;
				});
			},
		};
	}

	async function apply() {
		if (!plan || !context?.model) {
			return;
		}

		let writes = planToWrites(plan, excluded);

		if (!writes.length) {
			return;
		}

		cancelRef.current = false;
		setRunning(true);
		setStep('run');
		setProgress({ completed: 0, total: writes.length, elapsedMs: 0, etaMs: estimateDurationMs(writes.length) });

		let runResult = await runBulkWrites(context.model, writes, {
			onProgress: setProgress,
			isCancelled: () => cancelRef.current,
			...trackingOptions(writes),
		});

		logEvent('bulkEdit', {
			field: plan.field,
			operation: plan.operation,
			attempted: writes.length,
			succeeded: runResult.succeeded.length,
			failed: runResult.failed.length,
			cancelled: runResult.cancelled,
			recordIds: runResult.succeeded.slice(0, 50),
		});

		setResult(runResult);
		setRunning(false);
		setStep('result');
		onApplied();
	}

	async function undo() {
		if (!plan || !result || !context?.model) {
			return;
		}

		let writes = planToUndoWrites(plan, result.succeeded);

		if (!writes.length) {
			return;
		}

		cancelRef.current = false;
		setRunning(true);
		setUndoPlan(invertPlan(plan, result.succeeded));
		setProgress({ completed: 0, total: writes.length, elapsedMs: 0, etaMs: estimateDurationMs(writes.length) });
		setStep('run');

		let runResult = await runBulkWrites(context.model, writes, {
			onProgress: setProgress,
			isCancelled: () => cancelRef.current,
			...trackingOptions(writes),
		});

		logEvent('bulkEditUndo', {
			field: plan.field,
			operation: plan.operation,
			attempted: writes.length,
			succeeded: runResult.succeeded.length,
			failed: runResult.failed.length,
		});

		setUndoResult(runResult);
		setRunning(false);
		setStep('result');
		onApplied();
	}

	function renderInput() {
		if (!operationDef.inputType) {
			return (
				<Typography variant="body2" color="textSecondary">
					This action needs no value.
				</Typography>
			);
		}

		if (operationDef.inputType === 'datetime' || operationDef.inputType === 'date') {
			let isDateTime = operationDef.inputType === 'datetime';
			let Picker: any = isDateTime ? KeyboardDateTimePicker : KeyboardDatePicker;

			return (
				<MuiPickersUtilsProvider utils={MomentUtils}>
					<Picker
						clearable
						showTodayButton
						todayLabel="Today"
						variant="dialog"
						ampm={isDateTime}
						format={isDateTime ? 'YYYY-MM-DD hh:mm A' : 'YYYY-MM-DD'}
						label={operationDef.inputLabel}
						value={pickerValue}
						onChange={(date: Moment | null) => {
							setPickerValue(date);
							setInput(date && date.isValid() ? date.format(isDateTime ? DATE_TIME_FIELD_VALUE_FORMAT : 'YYYY-MM-DD') : '');
						}}
						fullWidth
						DialogProps={overlayProps}
					/>
				</MuiPickersUtilsProvider>
			);
		}

		return (
			<TextField
				fullWidth
				autoFocus
				type={operationDef.inputType === 'number' ? 'number' : 'text'}
				label={operationDef.inputLabel}
				value={input}
				onChange={(event) => setInput(event.target.value)}
				error={!!input && !!validationError}
				helperText={input ? validationError : ''}
			/>
		);
	}

	function renderBuild() {
		let filters = context?.filters ?? [];

		return (
			<>
				<Alert severity="info" className={classes.section}>
					<AlertTitle>
						{records.length} record{records.length === 1 ? '' : 's'} will be considered
					</AlertTitle>
					This is every record matching the filters below, not only the ones on screen.
					<Box mt={1} className={classes.chips}>
						{filters.length ? (
							filters.map((filter) => <Chip key={filter} size="small" label={filter} />)
						) : (
							<Chip size="small" label="No filters applied" />
						)}
					</Box>
				</Alert>

				{records.length > LARGE_SET_THRESHOLD && (
					<Alert severity="warning" className={classes.section}>
						That is a large set. Narrow the filters first if you only meant to change some of them.
					</Alert>
				)}

				<FormControl fullWidth className={classes.section}>
					<InputLabel>Field</InputLabel>
					<Select
						value={fieldKey}
						onChange={(event) => onFieldChange(event.target.value as string)}
						MenuProps={overlayProps}
					>
						{anumatiBulkFields.map((candidate) => (
							<MenuItem key={candidate.key} value={candidate.key}>
								{candidate.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				<FormControl fullWidth className={classes.section}>
					<InputLabel>Action</InputLabel>
					<Select
						value={operation}
						onChange={(event) => onOperationChange(event.target.value as BulkOperationId)}
						MenuProps={overlayProps}
					>
						{definition.operations.map((candidate) => (
							<MenuItem key={candidate.id} value={candidate.id}>
								{candidate.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				<Box className={classes.section}>{renderInput()}</Box>

				{operationDef.destructive && (
					<Alert severity="error">
						<AlertTitle>This cannot be undone</AlertTitle>
						Permanently deleted records are removed from the database for good.
					</Alert>
				)}
			</>
		);
	}

	function renderPreview() {
		if (!plan) {
			return null;
		}

		return (
			<>
				<Typography variant="subtitle1" gutterBottom>
					{describeChange(plan)}
				</Typography>

				<Box className={classes.counts}>
					<Chip color="primary" label={`${includedCount} will change`} />
					{plan.unchangedCount > 0 && (
						<Chip variant="outlined" label={`${plan.unchangedCount} already have this value`} />
					)}
					{plan.blockedCount > 0 && <Chip variant="outlined" label={`${plan.blockedCount} cannot change`} />}
				</Box>

				<Typography variant="body2" color="textSecondary" gutterBottom>
					{plan.destructive
						? 'These records are removed from the database entirely.'
						: `Nothing else on these records changes, only ${plan.fieldLabel}.`}{' '}
					{describeDuration(includedCount)}
				</Typography>

				<Box mb={1}>
					<Button size="small" onClick={() => setAllRows(true)}>
						Include all
					</Button>
					<Button size="small" onClick={() => setAllRows(false)}>
						Exclude all
					</Button>
				</Box>

				<Box className={classes.table}>
					<PreviewTable rows={plan.rows} excluded={excluded} onToggle={toggleRow} />
				</Box>

				{plan.destructive && (
					<Alert severity="error" style={{ marginTop: 16 }}>
						<AlertTitle>Permanent deletion</AlertTitle>
						Type <strong>{includedCount}</strong> to confirm you want to permanently delete that many records.
						<TextField
							fullWidth
							value={typedConfirm}
							onChange={(event) => setTypedConfirm(event.target.value)}
							placeholder={String(includedCount)}
							style={{ marginTop: 8 }}
						/>
					</Alert>
				)}
			</>
		);
	}

	/** The same preview table, now reporting live per record status. */
	function renderLiveTable() {
		// During an undo the reversal is what matters, so the rows are shown the other way round.
		let shown = undoPlan ?? plan;

		if (!shown) {
			return null;
		}

		return (
			<Box className={classes.table} mt={2}>
				<PreviewTable rows={shown.rows} excluded={excluded} onToggle={() => {}} statuses={rowStatus} errors={rowErrors} />
			</Box>
		);
	}

	function renderRun() {
		return (
			<Box py={1}>
				<RunProgressView
					title={undoPlan ? 'Undoing changes…' : 'Applying changes…'}
					progress={progress}
					cancelling={cancelRef.current}
				/>
				{renderLiveTable()}
			</Box>
		);
	}

	function renderResult() {
		let active = undoResult ?? result;

		if (!active) {
			return null;
		}

		let isUndo = !!undoResult;

		return (
			<>
				<RunResultAlert
					result={active}
					isUndo={isUndo}
					skippedCount={plan ? plan.unchangedCount + plan.blockedCount : 0}
					completeTitle="Bulk edit complete"
				/>

				{!isUndo && plan && isUndoable(plan) && result && result.succeeded.length > 0 && (
					<Alert severity="info" style={{ marginTop: 16 }}>
						<AlertTitle>Changed your mind?</AlertTitle>
						Undo writes the previous values back. Any edits made by someone else in the meantime would be
						overwritten.
					</Alert>
				)}

				{renderLiveTable()}
			</>
		);
	}

	let activeStepIndex = step === 'build' ? 0 : step === 'preview' ? 1 : 2;

	return (
		<Dialog
			open={open}
			onClose={running ? undefined : onClose}
			maxWidth="lg"
			fullWidth
			disableBackdropClick={running}
			disableEscapeKeyDown={running}
			// Set inline because Material's own modal class would otherwise win.
			style={{ zIndex: DIALOG_Z_INDEX }}
		>
			<DialogTitle>Bulk edit</DialogTitle>
			<DialogContent dividers>
				<Stepper activeStep={activeStepIndex} alternativeLabel>
					{STEPS.map((label) => (
						<Step key={label}>
							<StepLabel>{label}</StepLabel>
						</Step>
					))}
				</Stepper>

				{step === 'build' && renderBuild()}
				{step === 'preview' && renderPreview()}
				{step === 'run' && renderRun()}
				{step === 'result' && renderResult()}
			</DialogContent>
			<DialogActions>
				{step === 'build' && (
					<>
						<Button onClick={onClose}>Cancel</Button>
						<Button
							color="primary"
							variant="contained"
							disabled={!records.length || !!validationError}
							onClick={buildPreview}
						>
							Preview changes
						</Button>
					</>
				)}

				{step === 'preview' && (
					<>
						<Button onClick={() => setStep('build')}>Back</Button>
						<Button
							color={plan?.destructive ? 'secondary' : 'primary'}
							variant="contained"
							disabled={!includedCount || !destructiveReady}
							onClick={apply}
						>
							{plan?.destructive ? `Delete ${includedCount} records` : `Apply to ${includedCount} records`}
						</Button>
					</>
				)}

				{step === 'run' && (
					<Button
						color="secondary"
						disabled={cancelRef.current}
						onClick={() => {
							cancelRef.current = true;
							setProgress((current) => (current ? { ...current } : current));
						}}
					>
						Cancel
					</Button>
				)}

				{step === 'result' && (
					<>
						{!undoResult && plan && isUndoable(plan) && !!result?.succeeded.length && (
							<Button color="secondary" disabled={running} onClick={undo}>
								Undo
							</Button>
						)}
						<Button color="primary" variant="contained" onClick={onClose}>
							Close
						</Button>
					</>
				)}
			</DialogActions>
		</Dialog>
	);
}

export default BulkEditDialog;
